import React, { useState, useEffect } from "react";
import QuantityControls from "../quantityControls";
import { PurchaseRequestItem } from "../../../../../packages/contract/dtos/purchaseRequest";
import { useUpdatePurchaseRequest } from "../../hooks/useUpdatePurchaseRequest";
import { useDeletePurchaseRequestItem } from "../../hooks/useDeletePurchaseRequestItem";

interface CartItemProps {
  item: PurchaseRequestItem;
  draftPurchaseRequestID?: string;
  branchId: string;
  tenantId: string;
}

const CartItem: React.FC<CartItemProps> = ({
  item,
  draftPurchaseRequestID,
  branchId,
  tenantId,
}) => {
  const [inputValue, setInputValue] = useState(item.quantity.toString());

  // Sync inputValue with item.quantity when it changes
  useEffect(() => {
    setInputValue(item.quantity.toString());
  }, [item.quantity]);

  // Initialize the hooks
  const updatePurchaseRequestMutation = useUpdatePurchaseRequest({
    branchId: branchId || "",
    componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
    tenantId: tenantId,
    draftPurchaseRequestID: draftPurchaseRequestID!,
    minQuantity: item?.tenant_product?.minimum_quantity || 0,
    maxQuantity: item?.tenant_product?.maximum_quantity || 0,
    currentQuantity: item?.quantity || 0,
  });

  const deletePurchaseRequestItemMutation = useDeletePurchaseRequestItem({
    branchId: branchId || "",
    componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
    tenantId: tenantId,
  });

  const handleQuantityChange = async (quantity: number) => {
    try {
      // Update via API if we have the draft ID
      if (branchId) {
        await updatePurchaseRequestMutation.mutateAsync({
          branchId: branchId,
          componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
          tenantId: tenantId,
          body: {
            tenant_product_id: item?.tenant_product?.id?.toString()!,
            quantity: quantity,
          },
        });
      }
    } catch (error) {
      console.error("Failed to update quantity:", error);
    }
  };

  const handleRemove = async () => {
    try {
      // Delete via API if we have the branch ID
      if (branchId) {
        await deletePurchaseRequestItemMutation.mutateAsync({
          branchId: branchId,
          itemId: String(item?.id?.tenantProductId!),
          componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
          tenantId: tenantId,
        });
      }
    } catch (error) {
      console.error("Failed to remove item:", error);
    }
  };

  // Check if any mutation is pending
  const isUpdating = updatePurchaseRequestMutation.isPending;
  const isDeleting = deletePurchaseRequestItemMutation.isPending;

  return (
    <div className="flex items-start gap-4 py-2 px-1 border-b border-gray-200">
      {/* Product Image */}
      <div className="flex-shrink-0">
        <img
          src={
            item.tenant_product?.catalog_info?.product_images?.[0]?.image_path
          }
          alt={item.tenant_product?.catalog_info?.name}
          className="rounded-lg object-cover"
          height={80}
          width={80}
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-1 gap-1">
          <div className="flex-1">
            <p className="text-xs lg:text-sm font-medium text-gray-500">
              {
                item?.tenant_product?.catalog_info?.product_categories?.[0]
                  ?.category?.name
              }
            </p>
            <h3 className="text-xs lg:text-sm font-semibold text-gray-900 line-clamp-2">
              {[
                item?.tenant_product?.tenant_product_name,
                item?.tenant_product?.catalog_info?.name,
              ]
                .filter(Boolean)
                .join(" - ") || "Unnamed Product"}
            </h3>

            {/* Feature Variants */}
            {item?.tenant_product?.catalog_info?.feature_values &&
              item.tenant_product.catalog_info.feature_values.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {item?.tenant_product?.catalog_info?.feature_values.map(
                    (feature: any, index: number) => (
                      <span
                        key={feature.feature_id}
                        className="text-xs text-gray-600"
                      >
                        {feature.featureVariant.variant_name}
                        {index <
                          item?.tenant_product?.catalog_info?.feature_values
                            ?.length! -
                            1 && " - "}
                      </span>
                    )
                  )}
                </div>
              )}

            <p className="text-xs mt-1">
              min: {item?.tenant_product?.minimum_quantity} - max:{" "}
              {item?.tenant_product?.maximum_quantity}
            </p>
          </div>

          {/* Price Information */}
          <div className="">
            {/* <p className="text-sm text-gray-500 line-through">
              {item.price.toFixed(1)}
            </p> */}
            {/* <p className="text-sm font-semibold text-red-600">
              {item?.tenant_product?.tenant_product_prices?.[0]?.price}
            </p> */}
            <p className="text-sm font-semibold">
              {item?.tenant_product?.tenant_product_prices?.[0]?.price}{" "}
              {item?.tenant_product?.tenant_product_prices?.[0]?.currency?.code}
            </p>
          </div>
        </div>

        {/* Quantity Controls */}
        <QuantityControls
          item={item}
          quantity={item.quantity}
          onQuantityChange={handleQuantityChange}
          onRemove={handleRemove}
          minQuantity={item?.tenant_product?.minimum_quantity!}
          maxQuantity={item?.tenant_product?.maximum_quantity!}
          showDeleteButton={true}
          disabled={isUpdating || isDeleting}
          setQuantity={handleQuantityChange}
          inputValue={inputValue}
          setInputValue={setInputValue}
          resetOnInvalidInput={true}
        />
      </div>
    </div>
  );
};

export default CartItem;
